import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  X, Users, UserPlus, Send, Search, Clock, FileText, Plus, Trash2,
  ChevronRight, Check, Copy, Loader2, ArrowRightLeft, Settings, Shield,
  Activity, RefreshCw, MessageSquare, AlertCircle, Pill, ClipboardList,
  Stethoscope, Bell, HelpCircle, CheckCircle2, AlertTriangle, GitMerge
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { getHistoryDisplayTitle, formatHistoryTime } from '../../hooks/historyDisplayUtils';
import { toast } from 'sonner';
import axios from 'axios';
import { persistProfilePref } from '../../utils/persistProfilePref';

const API = window.__WR_API_BASE__;

// Relative time helper
const timeAgo = (isoStr) => {
  if (!isoStr) return '';
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const ACTION_LABELS = {
  shared_note: 'shared a note',
  started_draft: 'started working on',
  added_member: 'added',
  created_team: 'created team',
};

export const TeamBoardSlidePanel = ({
  isOpen,
  onClose,
  user,
  guestId,
  history,
  onLoadNote,
  autoShareTeamId,
  autoShareDrafts,
  toggleAutoShareTeam,
  toggleAutoShareDrafts,
  onUnreadChange,
  // Feb 28, 2026 — when global "Auto-Stream to Default Team" is ON in Profile,
  // we want every team pill to visually reflect "auto-share is ON" without
  // requiring per-team manual toggling (per user direction: "All teams I'm
  // assigned to"). Pass-through from useAutosaveSession.
  globalAutoStreamOn = false,
}) => {
  // Axios config with guest fallback
  const axConfig = { withCredentials: true, headers: guestId ? { 'X-Guest-ID': guestId } : {} };
  const [teams, setTeams] = useState([]);
  const [activeTeamId, setActiveTeamId] = useState(() => {
    // Prioritize autoShareTeamId from props so the toggle shows correctly on open
    const stored = localStorage.getItem('writeright_active_team');
    return stored || null;
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamNotes, setTeamNotes] = useState([]);
  const [teamActivity, setTeamActivity] = useState([]);
  const [myRole, setMyRole] = useState('member');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('board');
  const [searchQuery, setSearchQuery] = useState('');

  // Create team state
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [creating, setCreating] = useState(false);

  // Add member state
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('medic');
  const [addingMember, setAddingMember] = useState(false);

  // Share note state
  const [showSharePicker, setShowSharePicker] = useState(false);

  // Board summary state
  const [boardSummary, setBoardSummary] = useState(null);
  const [boardLoading, setBoardLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatMsgType, setChatMsgType] = useState('general');
  const chatScrollRef = useRef(null);
  
  // Unread tracking
  const lastSeenCountRef = useRef(0);
  const [teamDrafts, setTeamDrafts] = useState([]);
  
  // Scroll preservation for background polls
  const panelScrollRef = useRef(null);
  
  // Bulk merge selection
  const [selectedOverlaps, setSelectedOverlaps] = useState(new Set());
  const [bulkMerging, setBulkMerging] = useState(false);

  // Load teams on open
  useEffect(() => {
    if (isOpen && user) fetchTeams();
  }, [isOpen, user]);

  // Sync activeTeamId with autoShareTeamId on open — ensures toggle shows correctly
  useEffect(() => {
    if (isOpen && autoShareTeamId && !activeTeamId) {
      setActiveTeamId(autoShareTeamId);
      localStorage.setItem('writeright_active_team', autoShareTeamId);
      persistProfilePref({ active_team_id: autoShareTeamId });
    }
  }, [isOpen, autoShareTeamId, activeTeamId]);


  // Load team data when active team changes
  useEffect(() => {
    if (activeTeamId && isOpen) fetchTeamData(activeTeamId);
  }, [activeTeamId, isOpen]);

  // Load board data when board tab is selected
  useEffect(() => {
    if (activeTab === 'board' && activeTeamId) {
      fetchBoardSummary(activeTeamId);
      fetchChatMessages(activeTeamId);
      fetchTeamDrafts(activeTeamId);
    }
  }, [activeTab, activeTeamId]);

  // LIVE POLL: Chat messages every 6 seconds when panel is open
  useEffect(() => {
    if (!isOpen || !activeTeamId) return;
    const interval = setInterval(() => {
      fetchChatMessages(activeTeamId);
    }, 6000);
    return () => clearInterval(interval);
  }, [isOpen, activeTeamId]);

  // LIVE POLL: Notes + drafts every 10 seconds when panel is open (scroll-safe)
  useEffect(() => {
    if (!isOpen || !activeTeamId) return;
    const interval = setInterval(() => {
      // Save scroll position before fetching
      const scrollEl = panelScrollRef.current;
      const scrollTop = scrollEl ? scrollEl.scrollTop : 0;
      
      // Silent fetches — no loading spinners on background polls
      const silentFetch = async () => {
        try {
          const [membersRes, notesRes, activityRes] = await Promise.all([
            axios.get(`${API}/api/teams/${activeTeamId}/members`, axConfig).catch(() => null),
            axios.get(`${API}/api/teams/${activeTeamId}/notes`, axConfig).catch(() => null),
            axios.get(`${API}/api/teams/${activeTeamId}/activity`, axConfig).catch(() => null),
          ]);
          if (membersRes) {
            setTeamMembers(membersRes.data?.members || []);
            setMyRole(membersRes.data?.my_role || 'member');
          }
          if (notesRes) setTeamNotes(notesRes.data?.notes || []);
          if (activityRes) setTeamActivity(activityRes.data?.activities || []);
        } catch {}
        
        if (activeTab === 'board') {
          try {
            const [summaryRes, draftsRes] = await Promise.all([
              axios.get(`${API}/api/teams/${activeTeamId}/board-summary`, axConfig).catch(() => null),
              axios.get(`${API}/api/teams/${activeTeamId}/drafts`, axConfig).catch(() => null),
            ]);
            // No setBoardLoading — silent background update
            if (summaryRes) setBoardSummary(summaryRes.data);
            if (draftsRes) setTeamDrafts(draftsRes.data?.drafts || []);
          } catch {}
        }
        
        // Restore scroll position after React re-render
        requestAnimationFrame(() => {
          if (scrollEl && scrollTop > 0) {
            scrollEl.scrollTop = scrollTop;
          }
        });
      };
      silentFetch();
    }, 10000);
    return () => clearInterval(interval);
  }, [isOpen, activeTeamId, activeTab]);

  // BACKGROUND POLL: Unread count when panel is CLOSED (every 20s)
  useEffect(() => {
    if (isOpen || !activeTeamId || !user) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/api/teams/${activeTeamId}/chat?limit=1`, axConfig);
        const msgs = res.data?.messages || [];
        const totalCount = msgs.length > 0 ? (res.data?.total || msgs.length) : 0;
        // Fetch full count
        const fullRes = await axios.get(`${API}/api/teams/${activeTeamId}/chat?limit=50`, axConfig);
        const fullMsgs = fullRes.data?.messages || [];
        const unread = Math.max(0, fullMsgs.length - lastSeenCountRef.current);
        if (onUnreadChange) onUnreadChange(unread);
      } catch {}
    }, 20000);
    return () => clearInterval(interval);
  }, [isOpen, activeTeamId, user]);

  // Mark as seen when panel opens
  useEffect(() => {
    if (isOpen && chatMessages.length > 0) {
      lastSeenCountRef.current = chatMessages.length;
      if (onUnreadChange) onUnreadChange(0);
    }
  }, [isOpen, chatMessages.length]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/teams/list`, axConfig);
      const list = res.data?.teams || [];
      setTeams(list);
      if (list.length > 0) {
        const saved = localStorage.getItem('writeright_active_team');
        const validSaved = list.find(t => t.id === saved);
        const selected = validSaved ? saved : list[0].id;
        setActiveTeamId(selected);
        localStorage.setItem('writeright_active_team', selected);
        const selectedTeam = list.find(t => t.id === selected);
        persistProfilePref({
          active_team_id: selected,
          active_team_name: selectedTeam?.name || '',
        });
      }
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamData = async (teamId) => {
    try {
      const [membersRes, notesRes, activityRes] = await Promise.all([
        axios.get(`${API}/api/teams/${teamId}/members`, axConfig).catch(() => ({ data: { members: [], my_role: 'member' } })),
        axios.get(`${API}/api/teams/${teamId}/notes`, axConfig).catch(() => ({ data: { notes: [] } })),
        axios.get(`${API}/api/teams/${teamId}/activity`, axConfig).catch(() => ({ data: { activities: [] } })),
      ]);
      setTeamMembers(membersRes.data?.members || []);
      setMyRole(membersRes.data?.my_role || 'member');
      setTeamNotes(notesRes.data?.notes || []);
      setTeamActivity(activityRes.data?.activities || []);
    } catch (err) {
      console.error('Failed to fetch team data:', err);
    }
  };

  const fetchBoardSummary = async (teamId) => {
    setBoardLoading(true);
    try {
      const res = await axios.get(`${API}/api/teams/${teamId}/board-summary`, axConfig);
      setBoardSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch board summary:', err);
    } finally {
      setBoardLoading(false);
    }
  };

  const fetchChatMessages = async (teamId) => {
    try {
      const res = await axios.get(`${API}/api/teams/${teamId}/chat`, axConfig);
      setChatMessages(res.data?.messages || []);
    } catch (err) {
      console.error('Failed to fetch chat:', err);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || !activeTeamId) return;
    setChatSending(true);
    try {
      const res = await axios.post(`${API}/api/teams/${activeTeamId}/chat`, {
        message: chatInput.trim(),
        message_type: chatMsgType,
      }, axConfig);
      if (res.data?.message) {
        setChatMessages(prev => [...prev, res.data.message]);
        lastSeenCountRef.current = chatMessages.length + 1;
      }
      setChatInput('');
      setChatMsgType('general');
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setChatSending(false);
    }
  };

  const fetchTeamDrafts = async (teamId) => {
    try {
      const res = await axios.get(`${API}/api/teams/${teamId}/drafts`, axConfig);
      setTeamDrafts(res.data?.drafts || []);
    } catch (err) {
      console.error('Failed to fetch team drafts:', err);
    }
  };

  const claimDraft = async (draftId, draft) => {
    try {
      await axios.post(`${API}/api/teams/${activeTeamId}/claim-draft`, {
        draft_id: draftId,
      }, axConfig);
      toast.success(`Picked up draft: ${draft.title || 'Draft'}`);
      if (onLoadNote) onLoadNote(draft);
      fetchTeamDrafts(activeTeamId);
    } catch (err) {
      toast.error('Failed to claim draft');
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/teams/create`, { name: newTeamName.trim(), guest_id: guestId }, axConfig);
      const newTeam = res.data?.team;
      toast.success(`Team "${newTeamName}" created`);
      setNewTeamName('');
      setShowCreateTeam(false);
      await fetchTeams();
      if (newTeam?.id) {
        setActiveTeamId(newTeam.id);
        localStorage.setItem('writeright_active_team', newTeam.id);
        persistProfilePref({
          active_team_id: newTeam.id,
          active_team_name: newTeam.name || newTeamName.trim(),
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create team');
    } finally {
      setCreating(false);
    }
  };

  const handleAddMember = async () => {
    if (!newMemberEmail.trim() || !activeTeamId) return;
    setAddingMember(true);
    try {
      await axios.post(`${API}/api/teams/${activeTeamId}/add-member`, {
        email: newMemberEmail.trim(),
        name: newMemberName.trim() || undefined,
        role: newMemberRole,
      }, axConfig);
      toast.success(`Added ${newMemberEmail}`);
      setNewMemberEmail('');
      setNewMemberName('');
      setShowAddMember(false);
      fetchTeamData(activeTeamId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (email) => {
    if (!activeTeamId) return;
    try {
      await axios.delete(`${API}/api/teams/${activeTeamId}/remove-member/${email}`, axConfig);
      toast.success(`Removed ${email}`);
      fetchTeamData(activeTeamId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleDeleteTeam = async () => {
    if (!activeTeamId) return;
    const team = teams.find(t => t.id === activeTeamId);
    if (!window.confirm(`Delete team "${team?.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/teams/${activeTeamId}`, axConfig);
      toast.success('Team deleted');
      setActiveTeamId(null);
      localStorage.removeItem('writeright_active_team');
      persistProfilePref({ active_team_id: '', active_team_name: '' });
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete team');
    }
  };

  const handleShareNote = async (noteId) => {
    if (!activeTeamId) { toast.error('Select a team first'); return; }
    try {
      await axios.post(`${API}/api/teams/${activeTeamId}/share-note`, { note_id: noteId, guest_id: guestId }, axConfig);
      toast.success('Note shared to team');
      setShowSharePicker(false);
      fetchTeamData(activeTeamId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to share note');
    }
  };

  const switchTeam = (teamId) => {
    setActiveTeamId(teamId);
    localStorage.setItem('writeright_active_team', teamId);
    // Persist the team name so other surfaces (Profile Settings, auto-stream
    // toast) can show it without re-fetching.
    const team = teams.find(t => t.id === teamId);
    if (team?.name) localStorage.setItem('writeright_active_team_name', team.name);
    persistProfilePref({
      active_team_id: teamId,
      active_team_name: team?.name || '',
    });
    // Feb 22, 2026 — If Auto-Stream is enabled in Profile settings, keep the
    // auto-share team in lockstep with the active team so the streamer
    // doesn't silently turn off when she picks a new team.
    if (localStorage.getItem('writeright_auto_stream_default') === 'true') {
      localStorage.setItem('writeright_auto_share_team', teamId);
    }
  };

  const activeTeam = teams.find(t => t.id === activeTeamId);
  const myNotes = (history || []).filter(h => h.clinical_note && !h.is_draft).slice(0, 20);

  const filteredNotes = teamNotes.filter(n =>
    !searchQuery || (n.title || '').toLowerCase().includes(searchQuery.toLowerCase()) || (n.shared_by_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredMembers = teamMembers.filter(m =>
    !searchQuery || (m.user_name || '').toLowerCase().includes(searchQuery.toLowerCase()) || (m.user_email || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" onClick={onClose} data-testid="team-board-backdrop" />
      
      <div className="fixed top-0 right-0 h-full w-full sm:w-[440px] md:w-[500px] bg-card border-l border-border z-50 shadow-2xl flex flex-col animate-in slide-in-from-right duration-300" data-testid="team-board-panel">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-primary/10">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground text-base">Team Board</h2>
              <p className="text-[11px] text-muted-foreground">
                {teams.length} team{teams.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setShowCreateTeam(!showCreateTeam)} className="h-8 text-xs gap-1" data-testid="create-team-btn">
              <Plus className="w-3.5 h-3.5" />
              New Team
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="team-board-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Create Team Inline */}
        {showCreateTeam && (
          <div className="px-4 py-3 border-b border-border bg-secondary/20 space-y-2" data-testid="create-team-form">
            <p className="text-xs font-semibold text-foreground">Create New Team</p>
            <Input
              placeholder="Team name (e.g., Primary Care A)"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              className="h-9 text-sm"
              onKeyDown={(e) => e.key === 'Enter' && handleCreateTeam()}
              data-testid="new-team-name-input"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleCreateTeam} disabled={creating || !newTeamName.trim()} className="h-7 text-xs" data-testid="confirm-create-team">
                {creating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Create
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowCreateTeam(false)} className="h-7 text-xs">Cancel</Button>
            </div>
          </div>
        )}

        {/* Team Selector */}
        {teams.length > 0 && (
          <div className="px-4 py-2 border-b border-border/50 shrink-0">
            <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
              {teams.map(team => (
                <button
                  key={team.id}
                  onClick={() => switchTeam(team.id)}
                  className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                    activeTeamId === team.id
                      ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                      : 'bg-secondary/30 text-muted-foreground border-border hover:bg-secondary/60 hover:text-foreground'
                  }`}
                  data-testid={`team-pill-${team.id}`}
                >
                  {team.name}
                  <span className="ml-1.5 opacity-70">({team.member_count})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Auto-share toggles */}
        {activeTeamId && (
          <div className="px-4 py-2 border-b border-border/50 flex items-center gap-3 flex-wrap" data-testid="team-autoshare-row">
            {/* Feb 28, 2026 — when global auto-stream is ON, pills show as
                "on" for EVERY team and clicking nudges the user back to the
                Profile master switch instead of toggling per-team. This is
                the source-of-truth UX the user requested. */}
            <button
              onClick={() => {
                if (globalAutoStreamOn) {
                  toast.info('Auto-stream is globally ON — manage it in Profile › Settings.', { duration: 2200 });
                  return;
                }
                toggleAutoShareTeam && toggleAutoShareTeam(activeTeamId);
              }}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-medium transition-all border ${
                (globalAutoStreamOn || autoShareTeamId === activeTeamId)
                  ? 'bg-green-500/15 border-green-500/40 text-green-400'
                  : 'bg-secondary/30 border-border text-muted-foreground hover:text-foreground'
              }`}
              data-testid="auto-share-toggle"
              title={globalAutoStreamOn ? 'Global Auto-Stream is ON — all your teams' : ''}
            >
              <Send className={`w-3 h-3 ${(globalAutoStreamOn || autoShareTeamId === activeTeamId) ? 'text-green-400' : ''}`} />
              Auto-share notes
              {globalAutoStreamOn && <span className="text-[8px] px-1 rounded bg-green-500/30 text-green-300 font-bold">ALL TEAMS</span>}
              {(globalAutoStreamOn || autoShareTeamId === activeTeamId) && <Check className="w-3 h-3 text-green-400" />}
            </button>
            <button
              onClick={() => {
                if (globalAutoStreamOn) {
                  toast.info('Auto-stream is globally ON — manage it in Profile › Settings.', { duration: 2200 });
                  return;
                }
                toggleAutoShareDrafts && toggleAutoShareDrafts();
              }}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-medium transition-all border ${
                (globalAutoStreamOn || autoShareDrafts)
                  ? 'bg-blue-500/15 border-blue-500/40 text-blue-400'
                  : 'bg-secondary/30 border-border text-muted-foreground hover:text-foreground'
              }`}
              data-testid="draft-stream-toggle"
              title={globalAutoStreamOn ? 'Global Auto-Stream is ON — all your teams' : ''}
            >
              <ArrowRightLeft className={`w-3 h-3 ${(globalAutoStreamOn || autoShareDrafts) ? 'text-blue-400' : ''}`} />
              Stream drafts
              {(globalAutoStreamOn || autoShareDrafts) && <Check className="w-3 h-3 text-blue-400" />}
            </button>
          </div>
        )}

        {/* Tabs */}
        {activeTeamId && (
          <div className="flex border-b border-border shrink-0">
            {[
              { id: 'board', label: 'Board', icon: MessageSquare },
              { id: 'notes', label: 'Notes', icon: FileText, count: teamNotes.length },
              { id: 'activity', label: 'Activity', icon: Activity, count: teamActivity.length },
              { id: 'members', label: 'Members', icon: Users, count: teamMembers.length },
              { id: 'share', label: 'Share', icon: Send },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'text-primary border-primary'
                    : 'text-muted-foreground border-transparent hover:text-foreground'
                }`}
                data-testid={`team-tab-${tab.id}`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
                {tab.count != null && <Badge variant="secondary" className="text-[9px] h-4 px-1 ml-0.5">{tab.count}</Badge>}
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        {activeTeamId && (activeTab === 'notes' || activeTab === 'members') && (
          <div className="px-4 py-2 border-b border-border/50 shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder={activeTab === 'members' ? 'Search members...' : 'Search notes...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-8 text-sm bg-secondary/30"
                data-testid="team-search-input"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div ref={panelScrollRef} className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : teams.length === 0 && !showCreateTeam ? (
            <div className="text-center py-16 px-6">
              <Users className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground">No teams yet</p>
              <p className="text-xs text-muted-foreground mt-1 mb-4">Create a team to start collaborating with your medics and providers</p>
              <Button size="sm" onClick={() => setShowCreateTeam(true)} className="text-xs gap-1.5" data-testid="empty-create-team">
                <Plus className="w-3.5 h-3.5" />
                Create Your First Team
              </Button>
            </div>
          ) : !activeTeamId ? (
            <div className="text-center py-12">
              <p className="text-sm text-muted-foreground">Select a team above</p>
            </div>
          ) : (
            <>
              {/* Board Tab — Chat FIRST at top, then Summary */}
              {activeTab === 'board' && (
                <div className="p-3 space-y-3">
                  {/* Team Chat — ALWAYS at top for quick access */}
                  <div className="border border-border rounded-lg overflow-hidden" data-testid="team-chat-section">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-secondary/20">
                      <MessageSquare className="w-3.5 h-3.5 text-primary" />
                      <span className="text-xs font-semibold text-foreground">Team Messages</span>
                      <Badge variant="secondary" className="text-[9px] h-4 px-1 ml-auto">{chatMessages.length}</Badge>
                      <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => fetchChatMessages(activeTeamId)} data-testid="refresh-chat">
                        <RefreshCw className="w-3 h-3 text-muted-foreground" />
                      </Button>
                    </div>
                    <div ref={chatScrollRef} className="h-32 overflow-y-auto p-2 space-y-1.5 bg-secondary/10">
                      {chatMessages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                          <MessageSquare className="w-6 h-6 opacity-30 mb-1" />
                          <p className="text-[10px]">No messages yet</p>
                        </div>
                      ) : chatMessages.map((msg, i) => (
                        <div key={msg.id || i} className={`px-2 py-1.5 rounded-md ${
                          msg.message_type === 'urgent' ? 'bg-red-500/10 border border-red-500/30' :
                          msg.message_type === 'question' ? 'bg-blue-500/5 border border-blue-500/20' :
                          msg.message_type === 'update' ? 'bg-green-500/5 border border-green-500/20' :
                          'bg-background/50'
                        }`} data-testid={`chat-msg-${i}`}>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            {msg.message_type === 'urgent' && <AlertCircle className="w-3 h-3 text-red-500" />}
                            {msg.message_type === 'question' && <HelpCircle className="w-3 h-3 text-blue-500" />}
                            {msg.message_type === 'update' && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                            {(!msg.message_type || msg.message_type === 'general') && <MessageSquare className="w-3 h-3 text-muted-foreground" />}
                            <span className="text-[10px] font-semibold text-primary">{msg.author}</span>
                            {msg.message_type && msg.message_type !== 'general' && (
                              <Badge variant="outline" className={`text-[8px] px-1 h-3.5 ${
                                msg.message_type === 'urgent' ? 'border-red-500/40 text-red-400' :
                                msg.message_type === 'question' ? 'border-blue-500/40 text-blue-400' :
                                'border-green-500/40 text-green-400'
                              }`}>{msg.message_type}</Badge>
                            )}
                            <span className="text-[9px] text-muted-foreground/60 ml-auto">
                              {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                          </div>
                          <p className="text-xs text-foreground pl-4">{msg.message}</p>
                        </div>
                      ))}
                    </div>
                    {/* Message type pills + input */}
                    <div className="flex gap-1 px-2 py-1 border-t border-border/50 bg-secondary/5">
                      {[
                        { type: 'general', label: 'Msg', icon: MessageSquare, activeClass: 'bg-secondary text-foreground' },
                        { type: 'question', label: '?', icon: HelpCircle, activeClass: 'bg-blue-500/20 text-blue-400 border-blue-500/40' },
                        { type: 'update', label: 'Update', icon: CheckCircle2, activeClass: 'bg-green-500/20 text-green-400 border-green-500/40' },
                        { type: 'urgent', label: 'Urgent', icon: AlertCircle, activeClass: 'bg-red-500/20 text-red-400 border-red-500/40' },
                      ].map(({ type, label, icon: Icon, activeClass }) => (
                        <button
                          key={type}
                          onClick={() => setChatMsgType(type)}
                          className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium border transition-colors ${
                            chatMsgType === type ? activeClass : 'border-transparent text-muted-foreground hover:text-foreground'
                          }`}
                          data-testid={`chat-type-${type}`}
                        >
                          <Icon className="w-2.5 h-2.5" />
                          {label}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-1.5 p-2 border-t border-border">
                      <Input
                        placeholder={chatMsgType === 'urgent' ? 'Urgent message...' : chatMsgType === 'question' ? 'Ask a question...' : 'Type a message...'}
                        value={chatInput}
                        onChange={e => setChatInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
                        className={`h-8 text-xs ${chatMsgType === 'urgent' ? 'border-red-500/40' : ''}`}
                        data-testid="team-chat-input"
                      />
                      <Button size="sm" onClick={sendChatMessage} disabled={chatSending || !chatInput.trim()} 
                        className={`h-8 px-2.5 ${chatMsgType === 'urgent' ? 'bg-red-600 hover:bg-red-700' : ''}`} 
                        data-testid="team-chat-send">
                        {chatSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </div>

                  {boardLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <>
                      {/* Summary Cards — clickable, tap to view notes */}
                      {boardSummary?.summary && (
                        <div className="space-y-2" data-testid="board-summary-grid">
                          {/* Top row: Notes / Patients */}
                          <div className="flex gap-2">
                            <div className="flex-1 p-2.5 rounded-lg border bg-primary/10 border-primary/30">
                              <p className="text-lg font-bold text-foreground leading-none">{boardSummary.summary.unique_patients || boardSummary.summary.total_notes}</p>
                              <p className="text-[10px] text-muted-foreground">Unique Patients</p>
                            </div>
                            <div className="flex-1 p-2.5 rounded-lg border bg-secondary/50 border-border">
                              <p className="text-lg font-bold text-foreground leading-none">{boardSummary.summary.total_notes}</p>
                              <p className="text-[10px] text-muted-foreground">Versions Generated</p>
                            </div>
                          </div>
                          {/* Bottom row: Profiles, Meds, Referrals, Imaging */}
                          <div className="grid grid-cols-2 gap-2">
                          {[
                            { label: 'Profiles', count: boardSummary.summary.profiles, icon: Shield, color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/30' },
                            { label: 'Meds', count: boardSummary.summary.medications, icon: Pill, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30' },
                            { label: 'Referrals', count: boardSummary.summary.referrals, icon: ClipboardList, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/30' },
                            { label: 'Imaging', count: boardSummary.summary.imaging_orders, icon: Stethoscope, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30' },
                          ].map(item => (
                            <button 
                              key={item.label} 
                              className={`flex items-center gap-2 p-2.5 rounded-lg border ${item.bg} hover:opacity-80 transition-opacity cursor-pointer text-left`} 
                              data-testid={`board-summary-${item.label.toLowerCase()}`}
                              onClick={() => setActiveTab('notes')}
                            >
                              <item.icon className={`w-4 h-4 ${item.color}`} />
                              <div>
                                <p className="text-lg font-bold text-foreground leading-none">{item.count}</p>
                                <p className="text-[10px] text-muted-foreground">{item.label} proposed</p>
                              </div>
                            </button>
                          ))}
                          </div>
                        </div>
                      )}

                      {/* Patient Overlaps — Bulk Merge */}
                      {boardSummary?.patient_overlaps?.length > 0 && (
                        <div className="space-y-1.5" data-testid="patient-overlap-section">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                              Same-Patient Overlap ({boardSummary.patient_overlaps.length})
                            </p>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => {
                                  if (selectedOverlaps.size === boardSummary.patient_overlaps.length) {
                                    setSelectedOverlaps(new Set());
                                  } else {
                                    setSelectedOverlaps(new Set(boardSummary.patient_overlaps.map((_, i) => i)));
                                  }
                                }}
                                className="text-[9px] text-primary font-semibold hover:underline"
                                data-testid="select-all-overlaps"
                              >
                                {selectedOverlaps.size === boardSummary.patient_overlaps.length ? 'Deselect All' : 'Select All'}
                              </button>
                              {selectedOverlaps.size > 0 && (
                                <Button
                                  size="sm" variant="outline"
                                  disabled={bulkMerging}
                                  className="h-6 px-2 text-[9px] font-semibold border-green-500/40 text-green-400 hover:bg-green-500/10 gap-1"
                                  onClick={async () => {
                                    setBulkMerging(true);
                                    const toastId = toast.loading(`Merging ${selectedOverlaps.size} patient overlaps...`);
                                    let merged = 0;
                                    try {
                                      for (const idx of selectedOverlaps) {
                                        const overlap = boardSummary.patient_overlaps[idx];
                                        if (!overlap) continue;
                                        const overlapNotes = teamNotes.filter(n => 
                                          n.patient_label === overlap.patient_label || 
                                          (n.chief_complaint && overlap.patient_label?.includes(n.chief_complaint))
                                        );
                                        if (overlapNotes.length < 2) continue;
                                        try {
                                          await axios.post(`${API}/api/merge-notes`, {
                                            my_note: overlapNotes[0]?.clinical_note || overlapNotes[0]?.notes || '',
                                            teammate_note: overlapNotes.slice(1).map(n => n.clinical_note || n.notes || '').join('\n\n'),
                                            icd10_codes: overlapNotes.flatMap(n => n.icd10_suggestions || []),
                                          }, axConfig);
                                          merged++;
                                        } catch {}
                                      }
                                      toast.dismiss(toastId);
                                      toast.success(`Merged ${merged}/${selectedOverlaps.size} patient overlaps`);
                                      setSelectedOverlaps(new Set());
                                      fetchBoardSummary(activeTeamId);
                                    } catch {
                                      toast.dismiss(toastId);
                                      toast.error('Bulk merge failed');
                                    }
                                    setBulkMerging(false);
                                  }}
                                  data-testid="bulk-merge-btn"
                                >
                                  {bulkMerging ? <Loader2 className="w-3 h-3 animate-spin" /> : <GitMerge className="w-3 h-3" />}
                                  Merge {selectedOverlaps.size} Selected
                                </Button>
                              )}
                            </div>
                          </div>
                          {boardSummary.patient_overlaps.map((overlap, i) => (
                            <div 
                              key={`overlap-${overlap.patient_label}-${i}`} 
                              className={`flex items-start gap-2 p-2.5 rounded-lg border transition-colors cursor-pointer ${
                                selectedOverlaps.has(i) 
                                  ? 'border-green-500/50 bg-green-500/10' 
                                  : 'border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20'
                              }`}
                              data-testid={`overlap-${i}`}
                            >
                              <input 
                                type="checkbox" 
                                checked={selectedOverlaps.has(i)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setSelectedOverlaps(prev => {
                                    const next = new Set(prev);
                                    if (next.has(i)) next.delete(i); else next.add(i);
                                    return next;
                                  });
                                }}
                                className="mt-0.5 rounded border-amber-500/40 text-green-500 focus:ring-green-500/20"
                                data-testid={`overlap-check-${i}`}
                              />
                              <button 
                                className="flex-1 text-left"
                                onClick={() => {
                                  setSearchQuery(overlap.patient_label || '');
                                  setActiveTab('notes');
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium text-foreground">{overlap.patient_label || 'Unknown'}</span>
                                  <Badge variant="outline" className="text-[9px] border-amber-500/40 text-amber-400">{overlap.note_count} charts</Badge>
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                  Charted by: {overlap.charted_by.join(', ')}
                                </p>
                                {overlap.duplicate_meds.length > 0 && (
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    <span className="text-[9px] text-red-400 font-semibold">Dup meds:</span>
                                    {overlap.duplicate_meds.map((med, mi) => (
                                      <Badge key={mi} variant="outline" className="text-[9px] border-red-500/40 text-red-400">{med}</Badge>
                                    ))}
                                  </div>
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* No data state */}
                      {boardSummary && boardSummary.summary?.total_notes === 0 && (
                        <div className="text-center py-6">
                          <MessageSquare className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
                          <p className="text-xs text-muted-foreground">No shared notes yet. Share some notes to see summaries here.</p>
                        </div>
                      )}

                      {/* Team Drafts — pick up each other's work */}
                      {teamDrafts.length > 0 && (
                        <div className="border border-border rounded-lg overflow-hidden" data-testid="team-drafts-section">
                          <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-secondary/20">
                            <FileText className="w-3.5 h-3.5 text-blue-400" />
                            <span className="text-xs font-semibold text-foreground">Team Drafts</span>
                            <Badge variant="secondary" className="text-[9px] h-4 px-1 ml-auto">{teamDrafts.length}</Badge>
                          </div>
                          <div className="max-h-48 overflow-y-auto p-2 space-y-1.5">
                            {teamDrafts.map((draft, i) => (
                              <div key={draft.draft_id || i} className="flex items-center gap-2 p-2 rounded-md border border-border bg-background/50 hover:bg-secondary/30 transition-colors" data-testid={`team-draft-${i}`}>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-foreground truncate">{draft.title || 'Untitled Draft'}</p>
                                  <p className="text-[10px] text-muted-foreground">
                                    {draft.shared_by_name || 'Unknown'} &middot; {timeAgo(draft.shared_at)}
                                    {draft.claimed_by_name && (
                                      <span className="text-green-400 ml-1">
                                        &middot; Picked up by {draft.claimed_by_name}
                                      </span>
                                    )}
                                  </p>
                                </div>
                                {!draft.claimed_by ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => claimDraft(draft.draft_id, draft)}
                                    className="h-6 text-[10px] px-2 gap-1 border-blue-500/40 text-blue-400 hover:bg-blue-500/10 shrink-0"
                                    data-testid={`claim-draft-${i}`}
                                  >
                                    <ArrowRightLeft className="w-3 h-3" />
                                    Pick Up
                                  </Button>
                                ) : (
                                  <Badge variant="outline" className="text-[9px] border-green-500/40 text-green-400 shrink-0">
                                    <Check className="w-2.5 h-2.5 mr-0.5" />
                                    Claimed
                                  </Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Notes Tab */}
              {activeTab === 'notes' && (
                <div className="p-3 space-y-2">
                  {filteredNotes.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No shared notes in this team</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">Share a note from the Share tab</p>
                    </div>
                  ) : (
                    filteredNotes.map((note, i) => (
                      <div
                        key={note.id || i}
                        className="p-3 rounded-lg border border-border bg-secondary/20 hover:bg-secondary/40 transition-colors cursor-pointer"
                        onClick={() => onLoadNote && onLoadNote(note)}
                        data-testid={`team-note-${i}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-medium text-foreground truncate">{note.title || 'Shared Note'}</p>
                              {note.is_draft && <Badge variant="outline" className="text-[9px] border-amber-500/40 text-amber-400 shrink-0">Draft</Badge>}
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                              by {note.shared_by_name || 'Unknown'}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3 text-muted-foreground" />
                              <span className="text-[10px] text-muted-foreground">
                                {timeAgo(note.shared_at)}
                              </span>
                            </div>
                            <p className="text-[9px] text-muted-foreground/60 mt-0.5">
                              {note.shared_at ? new Date(note.shared_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                            </p>
                          </div>
                        </div>
                        {note.clinical_note && (
                          <p className="text-xs text-muted-foreground/70 mt-1.5 line-clamp-2">{note.clinical_note.substring(0, 120)}...</p>
                        )}
                        {note.icd10_suggestions?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {note.icd10_suggestions.slice(0, 3).map((c, ci) => (
                              <Badge key={ci} variant="outline" className="text-[9px] font-mono">{c.code}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Activity Tab */}
              {activeTab === 'activity' && (
                <div className="p-3 space-y-1">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-foreground">Recent Activity</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => fetchTeamData(activeTeamId)}
                      className="h-6 w-6 p-0"
                      data-testid="refresh-activity"
                    >
                      <RefreshCw className="w-3 h-3 text-muted-foreground" />
                    </Button>
                  </div>
                  {teamActivity.length === 0 ? (
                    <div className="text-center py-8">
                      <Activity className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No activity yet</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">Share notes or add members to see activity</p>
                    </div>
                  ) : (
                    <div className="relative">
                      {/* Timeline line */}
                      <div className="absolute left-3 top-2 bottom-2 w-px bg-border" />
                      {teamActivity.map((act, i) => (
                        <div key={act.id || i} className="relative flex gap-3 py-2" data-testid={`activity-item-${i}`}>
                          {/* Dot */}
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 z-10 ${
                            act.action === 'shared_note' ? 'bg-green-500/20' :
                            act.action === 'started_draft' ? 'bg-blue-500/20' :
                            act.action === 'added_member' ? 'bg-purple-500/20' :
                            'bg-primary/20'
                          }`}>
                            {act.action === 'shared_note' && <Send className="w-3 h-3 text-green-400" />}
                            {act.action === 'started_draft' && <FileText className="w-3 h-3 text-blue-400" />}
                            {act.action === 'added_member' && <UserPlus className="w-3 h-3 text-purple-400" />}
                            {act.action === 'created_team' && <Plus className="w-3 h-3 text-primary" />}
                          </div>
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-foreground">
                              <span className="font-semibold">{act.user_name}</span>
                              {' '}
                              <span className="text-muted-foreground">{ACTION_LABELS[act.action] || act.action}</span>
                              {act.detail && (
                                <span className="font-medium text-foreground"> {act.detail}</span>
                              )}
                            </p>
                            <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                              {timeAgo(act.timestamp)}
                              {act.timestamp && ` \u00B7 ${new Date(act.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Members Tab */}
              {activeTab === 'members' && (
                <div className="p-3 space-y-2">
                  {myRole === 'provider' && (
                    <>
                      {!showAddMember ? (
                        <Button variant="outline" size="sm" onClick={() => setShowAddMember(true)} className="w-full h-9 text-xs gap-1.5 border-dashed" data-testid="add-member-btn">
                          <UserPlus className="w-3.5 h-3.5" />
                          Add Team Member
                        </Button>
                      ) : (
                        <div className="p-3 rounded-lg border border-primary/30 bg-primary/5 space-y-2" data-testid="add-member-form">
                          <Input
                            placeholder="Member email"
                            value={newMemberEmail}
                            onChange={(e) => setNewMemberEmail(e.target.value)}
                            className="h-8 text-sm"
                            data-testid="new-member-email"
                          />
                          <Input
                            placeholder="Display name (optional)"
                            value={newMemberName}
                            onChange={(e) => setNewMemberName(e.target.value)}
                            className="h-8 text-sm"
                            data-testid="new-member-name"
                          />
                          <select
                            value={newMemberRole}
                            onChange={(e) => setNewMemberRole(e.target.value)}
                            className="w-full h-8 px-2 rounded-md border border-border bg-input text-sm"
                            data-testid="new-member-role"
                          >
                            <option value="medic">Medic</option>
                            <option value="nurse">Nurse</option>
                            <option value="provider">Provider</option>
                            <option value="admin">Admin</option>
                          </select>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleAddMember} disabled={addingMember || !newMemberEmail.trim()} className="h-7 text-xs" data-testid="confirm-add-member">
                              {addingMember ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <UserPlus className="w-3 h-3 mr-1" />}
                              Add
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setShowAddMember(false)} className="h-7 text-xs">Cancel</Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {filteredMembers.map((member, i) => (
                    <div
                      key={member.id || i}
                      className="flex items-center gap-3 p-3 rounded-lg border border-border bg-secondary/20"
                      data-testid={`team-member-${i}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-primary">{(member.user_name || 'U').charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{member.user_name || 'Unknown'}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{member.user_email}</p>
                      </div>
                      <Badge variant="outline" className={`text-[10px] shrink-0 ${
                        member.role === 'provider' ? 'border-blue-500/40 text-blue-400' :
                        member.role === 'admin' ? 'border-amber-500/40 text-amber-400' :
                        'border-border'
                      }`}>
                        {member.role}
                      </Badge>
                      {myRole === 'provider' && member.user_id !== user?.user_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveMember(member.user_email)}
                          className="h-7 w-7 p-0 text-destructive/60 hover:text-destructive"
                          data-testid={`remove-member-${i}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}

                  {/* Team settings (creator only) */}
                  {activeTeam?.creator_id === user?.user_id && (
                    <div className="pt-3 mt-3 border-t border-border">
                      <Button variant="ghost" size="sm" onClick={handleDeleteTeam} className="w-full h-8 text-xs text-destructive/70 hover:text-destructive gap-1.5" data-testid="delete-team-btn">
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete Team
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Share Tab */}
              {activeTab === 'share' && (
                <div className="p-3 space-y-3">
                  <div className="px-2">
                    <p className="text-xs font-semibold text-foreground mb-1">Share to: <span className="text-primary">{activeTeam?.name}</span></p>
                    <p className="text-[10px] text-muted-foreground">Select a completed note to share with this team</p>
                  </div>
                  {myNotes.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground">No completed notes to share. Generate some documents first.</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
                      {myNotes.map((note, i) => (
                        <div
                          key={note.id || i}
                          className="flex items-center gap-2 p-2.5 rounded-lg border border-border bg-secondary/20 hover:bg-secondary/40 transition-colors"
                          data-testid={`shareable-note-${i}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-foreground truncate">
                              {getHistoryDisplayTitle(note)}
                            </p>
                            <p className="text-[10px] text-muted-foreground">
                              {note.timestamp ? formatHistoryTime(note.timestamp, note.patient_label) + ' — ' + new Date(note.timestamp).toLocaleDateString() : ''}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleShareNote(note.id)}
                            className="h-7 text-[10px] gap-1 shrink-0 border-primary/40 text-primary hover:bg-primary/10"
                            data-testid={`share-note-btn-${i}`}
                          >
                            <Send className="w-3 h-3" />
                            Share
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default TeamBoardSlidePanel;
